import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { MarkdownExport } from '../components/ExportButtons.js';
import { useMediaQuery } from '../components/useMediaQuery.js';
import { FixPanel } from '../components/FixPanel.js';
import { HelpButton } from '../components/HelpButton.js';
import { AlertIcon, CheckIcon, ChevronDownIcon, InfoIcon } from '../components/icons.js';
import { PageTitle } from '../components/Layouts.js';
import { useLocale } from '../components/site.js';
import {
  Button,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  SeverityBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';
import { SEVERITIES } from './Overview.js';

export const CATEGORIES = [
  'availability',
  'security',
  'links',
  'seo',
  'performance',
  'email',
  'compliance',
] as const;
type Category = (typeof CATEGORIES)[number];
type Status = 'open' | 'ignored' | 'resolved';
type IgnoreReason = 'not_relevant' | 'will_fix_later' | 'false_positive';
const REASONS: IgnoreReason[] = ['not_relevant', 'will_fix_later', 'false_positive'];

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | undefined =>
  allowed.find((a) => a === value);

/** One occurrence: the affected address with ignore / restore. */
function Occurrence({
  finding,
  status,
  locked,
}: {
  finding: Schemas['Finding'];
  status: Status;
  locked: boolean;
}) {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const reasonId = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<IgnoreReason>('not_relevant');
  const change = useMutation({
    mutationFn: (body: { status: 'ignored' | 'open'; ignoreReason?: IgnoreReason }) =>
      unwrap(
        api.PATCH('/findings/{findingId}', {
          params: { path: { findingId: finding.id } },
          body,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
  return (
    <li className="flex flex-col gap-2 border-t border-slate-100 py-2 first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={`min-w-0 break-all text-slate-800 ${finding.affectedUrl ? 'font-mono text-sm' : ''}`}
        >
          {finding.affectedUrl ?? t(locked ? 'findings.lockedPlace' : 'findings.wholeSite')}
        </span>
        {status === 'open' && !asking ? (
          <Button variant="secondary" onClick={() => setAsking(true)}>
            {t('findings.ignore')}
          </Button>
        ) : null}
        {status === 'ignored' ? (
          <Button
            variant="secondary"
            busy={change.isPending}
            onClick={() => change.mutate({ status: 'open' })}
          >
            {t('findings.restore')}
          </Button>
        ) : null}
      </div>
      {asking ? (
        <form
          className="flex flex-col gap-3 rounded-md bg-slate-50 p-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ status: 'ignored', ignoreReason: reason });
          }}
        >
          <SelectField
            id={reasonId}
            label={t('findings.ignoreReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value as IgnoreReason)}
          >
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`findings.reasons.${r}`)}
              </option>
            ))}
          </SelectField>
          <Button type="submit" busy={change.isPending}>
            {t('findings.ignoreConfirm')}
          </Button>
          <Button variant="secondary" onClick={() => setAsking(false)}>
            {t('app.cancel')}
          </Button>
        </form>
      ) : null}
      {finding.ignoreReason && status === 'ignored' ? (
        <p className="text-sm text-slate-700">{t(`findings.reasons.${finding.ignoreReason}`)}</p>
      ) : null}
      {change.isError ? <Notice kind="error">{errorMessage(change.error, t)}</Notice> : null}
    </li>
  );
}

/** Coloured left edge of a problem card by severity (T-83). */
const SEVERITY_EDGE: Record<Severity, string> = {
  critical: 'border-l-red-600',
  high: 'border-l-orange-500',
  medium: 'border-l-amber-400',
  low: 'border-l-slate-400',
  info: 'border-l-blue-300',
};

/** A problem card: what, impact, action, how to fix it yourself, help from us. */
function GroupCard({
  group,
  status,
  initiallyOpen,
  fixed = false,
}: {
  group: Schemas['FindingGroup'];
  status: Status;
  initiallyOpen: boolean;
  /** Detail pane of the wide layout (T-84): always open, no toggle. */
  fixed?: boolean;
}) {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [toggled, setOpen] = useState(initiallyOpen);
  const open = fixed || toggled;
  const Outer = fixed ? 'div' : 'li';
  const panelId = useId();
  const e = group.explanation;
  const since = group.items.reduce<string | null>(
    (min, f) => (min === null || f.firstSeenAt < min ? f.firstSeenAt : min),
    null,
  );
  return (
    <Outer>
      <section
        className={`rounded-xl border border-l-4 border-slate-200 bg-white p-4 shadow-sm sm:p-5 ${SEVERITY_EDGE[group.severity]}`}
      >
        {fixed ? (
          <h2 className="flex flex-wrap items-center gap-2 text-xl font-bold">
            <SeverityBadge severity={group.severity} />
            <span className="min-w-0 flex-1">{group.title}</span>
            {group.count > 1 ? (
              <span className="text-sm font-normal text-slate-700">
                {t('findings.count', { count: group.count })}
              </span>
            ) : null}
          </h2>
        ) : (
          <h2 className="text-lg font-semibold">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => {
                if (!open) track('finding_viewed', { ruleCode: group.ruleCode });
                setOpen(!open);
              }}
              className="flex min-h-11 w-full flex-wrap items-center gap-2 text-left"
            >
              <SeverityBadge severity={group.severity} />
              <span className="min-w-0 flex-1">{group.title}</span>
              {group.count > 1 ? (
                <span className="text-sm font-normal text-slate-700">
                  {t('findings.count', { count: group.count })}
                </span>
              ) : null}
              <span
                aria-hidden="true"
                className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
              >
                <ChevronDownIcon />
              </span>
            </button>
          </h2>
        )}
        <div id={panelId} hidden={!open} className="mt-3 flex flex-col gap-4">
          {since ? (
            <p className="text-sm text-slate-600">
              {t('findings.since', { date: formatDate(since, i18n.language, timeZone) })}
            </p>
          ) : null}
          {e ? (
            <dl
              className={`grid gap-3 leading-relaxed wrap-anywhere ${fixed ? '' : 'md:grid-cols-3'}`}
            >
              <div className="rounded-lg bg-slate-50 p-4">
                <dt className="mb-1 flex items-center gap-2 font-bold text-slate-800">
                  <InfoIcon />
                  {t('findings.what')}
                </dt>
                <dd className="text-slate-900">{e.what}</dd>
              </div>
              <div className="rounded-lg bg-amber-50 p-4">
                <dt className="mb-1 flex items-center gap-2 font-bold text-amber-900">
                  <AlertIcon />
                  {t('findings.impact')}
                </dt>
                <dd className="text-slate-900">{e.impact}</dd>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <dt className="mb-1 flex items-center gap-2 font-bold text-blue-900">
                  <CheckIcon />
                  {t('findings.action')}
                </dt>
                <dd className="font-medium text-slate-900">{e.action}</dd>
              </div>
            </dl>
          ) : null}
          {e?.legalDisclaimer ? (
            <p className="flex items-start gap-2 text-sm text-slate-600">
              <span className="mt-0.5 shrink-0">
                <InfoIcon />
              </span>
              {e.legalDisclaimer}
            </p>
          ) : null}
          {e && e.diySteps.length > 0 ? (
            <details
              className="rounded-lg border border-slate-200 px-4 py-1 open:pb-4"
              onToggle={(e) => {
                if (e.currentTarget.open) track('diy_steps_opened', { ruleCode: group.ruleCode });
              }}
            >
              <summary className="min-h-11 cursor-pointer py-2 font-semibold text-blue-900">
                {t('findings.diy')}
              </summary>
              <ol className="ml-5 flex list-decimal flex-col gap-1.5 leading-relaxed">
                {e.diySteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </details>
          ) : null}
          {status === 'open' && group.guidance === 'full' ? (
            <FixPanel siteId={siteId} ruleCode={group.ruleCode} />
          ) : null}
          {group.guidance === 'summary' ? (
            <Notice kind="info">
              <p>{t('findings.summaryOnly')}</p>
              <LinkButton to="/billing" variant="secondary" size="sm" className="mt-2">
                {t('findings.summaryCta')}
              </LinkButton>
            </Notice>
          ) : null}
          {group.guidance === 'locked' ? (
            <Notice kind="info">
              <p>{t('findings.lockedNote')}</p>
              <LinkButton
                to={`/sites/${siteId}/settings`}
                variant="secondary"
                size="sm"
                className="mt-2"
              >
                {t('findings.lockedCta')}
              </LinkButton>
            </Notice>
          ) : null}
          {group.service && status === 'open' ? (
            <div>
              <HelpButton
                siteId={siteId}
                service={group.service}
                {...(group.items[0] ? { findingId: group.items[0].id } : {})}
                ruleCode={group.ruleCode}
              />
            </div>
          ) : null}
          <div>
            <h3 className="mb-1 font-bold text-slate-800">{t('findings.affected')}</h3>
            <ul className="rounded-lg border border-slate-200 px-3">
              {group.items.map((f) => (
                <Occurrence
                  key={f.id}
                  finding={f}
                  status={status}
                  locked={group.guidance === 'locked'}
                />
              ))}
            </ul>
            {group.count > group.items.length ? (
              <p className="text-sm text-slate-700">
                {t('findings.more', { count: group.count - group.items.length })}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </Outer>
  );
}

/** "Alle 17 · Kritisch 1 · Hoch 2 …": one button per level that has problems (T-84). */
function SeverityChips({
  groups,
  active,
  onPick,
}: {
  groups: readonly Schemas['FindingGroup'][];
  active: Severity | undefined;
  onPick: (severity: Severity | undefined) => void;
}) {
  const { t } = useTranslation();
  const total = groups.reduce((n, g) => n + g.count, 0);
  const per = (s: Severity) =>
    groups.filter((g) => g.severity === s).reduce((n, g) => n + g.count, 0);
  const chip = (on: boolean) =>
    `inline-flex min-h-10 items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold ${on ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`;
  return (
    <div
      role="group"
      aria-label={t('findings.filters.severity')}
      className="mb-4 flex flex-wrap gap-2"
    >
      <button
        type="button"
        aria-pressed={!active}
        onClick={() => onPick(undefined)}
        className={chip(!active)}
      >
        {t('findings.filters.all')} <span className="tabular-nums">{total}</span>
      </button>
      {SEVERITIES.filter((s) => per(s) > 0).map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={active === s}
          onClick={() => onPick(active === s ? undefined : s)}
          className={chip(active === s)}
        >
          <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${SEVERITY_DOT[s]}`} />
          {t(`severity.${s}`)} <span className="tabular-nums">{per(s)}</span>
        </button>
      ))}
    </div>
  );
}

const SEVERITY_DOT: Record<Severity, string> = {
  critical: 'bg-red-600',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-400',
  info: 'bg-blue-300',
};

type Urgency = 'now' | 'soon' | 'when_possible' | 'tip';

/** When to act when a problem has no explanation (it carries its own urgency otherwise). */
const URGENCY_OF: Record<Severity, Urgency> = {
  critical: 'now',
  high: 'soon',
  medium: 'soon',
  low: 'when_possible',
  info: 'tip',
};

const urgencyOf = (g: Schemas['FindingGroup']): Urgency =>
  g.explanation?.urgency ?? URGENCY_OF[g.severity];

/** Left column of the wide layout: problems by urgency, the selected one marked (T-84). */
function ProblemIndex({
  groups,
  selected,
  onSelect,
}: {
  groups: readonly Schemas['FindingGroup'][];
  selected: string;
  onSelect: (ruleCode: string) => void;
}) {
  const { t } = useTranslation();
  const sections = (['now', 'soon', 'when_possible', 'tip'] as const)
    .map((u) => [u, groups.filter((g) => urgencyOf(g) === u)] as const)
    .filter(([, list]) => list.length > 0);
  return (
    <nav
      aria-label={t('findings.title')}
      className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white"
    >
      {sections.map(([u, list]) => (
        <div key={u}>
          <h2 className="bg-slate-50 px-4 py-2 text-xs font-bold tracking-wide text-slate-700 uppercase">
            {t(`report.urgency.${u}`)} · {list.length}
          </h2>
          <ul>
            {list.map((g) => {
              const current = g.ruleCode === selected;
              return (
                <li key={g.ruleCode} className="border-t border-slate-100">
                  <button
                    type="button"
                    aria-current={current ? 'true' : undefined}
                    onClick={() => onSelect(g.ruleCode)}
                    className={`flex w-full items-start gap-2 px-4 py-3 text-left ${current ? 'bg-blue-50 shadow-[inset_4px_0_0_#1d4ed8]' : 'hover:bg-slate-50'}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${SEVERITY_DOT[g.severity]}`}
                    />
                    <span className={`min-w-0 flex-1 ${current ? 'font-semibold' : ''}`}>
                      <span className="sr-only">{t(`severity.${g.severity}`)}: </span>
                      {g.title}
                    </span>
                    {g.count > 1 ? (
                      <span className="shrink-0 text-sm text-slate-600 tabular-nums">
                        {g.count}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** S4 "Probleme": open problems grouped by rule, most severe first. */
export function Findings() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const [params, setParams] = useSearchParams();
  const category = oneOf(params.get('category'), CATEGORIES);
  const severity = oneOf(params.get('severity'), SEVERITIES);
  const status = oneOf(params.get('status'), ['open', 'ignored', 'resolved'] as const) ?? 'open';
  const openRule = params.get('open');
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('open');
    setParams(next, { replace: true });
  };
  const findings = useQuery({
    queryKey: ['site', siteId, 'findings', category, status, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: {
            path: { siteId },
            query: {
              grouped: 'true',
              status,
              locale,
              limit: 200,
              ...(category ? { category } : {}),
            },
          },
        }),
      ),
  });
  const all = findings.data?.groups ?? [];
  // Severity filters on the client: the chips show how many problems each level has (T-84).
  const groups = severity ? all.filter((g) => g.severity === severity) : all;
  const wide = useMediaQuery('(min-width: 1280px)');
  const selected = groups.find((g) => g.ruleCode === openRule) ?? groups[0];
  const select = (ruleCode: string) => {
    track('finding_viewed', { ruleCode });
    const next = new URLSearchParams(params);
    next.set('open', ruleCode);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageTitle
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkdownExport siteId={siteId} />
            <LinkButton
              to={`/sites/${siteId}/report`}
              variant="secondary"
              className="whitespace-nowrap"
            >
              {t('report.open')}
            </LinkButton>
          </div>
        }
      >
        {t('findings.title')}
      </PageTitle>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <SelectField
          label={t('findings.filters.status')}
          value={status}
          onChange={(e) => setFilter('status', e.target.value === 'open' ? '' : e.target.value)}
        >
          {(['open', 'ignored', 'resolved'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`findings.statuses.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('findings.filters.category')}
          value={category ?? ''}
          onChange={(e) => setFilter('category', e.target.value)}
        >
          <option value="">{t('findings.filters.all')}</option>
          {CATEGORIES.map((c: Category) => (
            <option key={c} value={c}>
              {t(`site.tiles.${c}`)}
            </option>
          ))}
        </SelectField>
      </div>
      {findings.isPending ? <Loading /> : null}
      {findings.isError ? (
        <LoadError
          message={errorMessage(findings.error, t)}
          onRetry={() => void findings.refetch()}
        />
      ) : null}
      {findings.data && groups.length === 0 ? (
        <Notice kind="success">{t(`findings.empty.${status}`)}</Notice>
      ) : null}
      {all.length > 0 ? (
        <SeverityChips
          groups={all}
          active={severity}
          onPick={(value) => setFilter('severity', value ?? '')}
        />
      ) : null}
      {groups.length > 0 && wide && selected ? (
        <div className="grid grid-cols-[minmax(0,19rem)_minmax(0,1fr)] items-start gap-5">
          <ProblemIndex groups={groups} selected={selected.ruleCode} onSelect={select} />
          <GroupCard
            key={`${selected.ruleCode}-${status}`}
            group={selected}
            status={status}
            initiallyOpen
            fixed
          />
        </div>
      ) : groups.length > 0 ? (
        <ul className="flex flex-col gap-4">
          {groups.map((g, i) => (
            <GroupCard
              key={`${g.ruleCode}-${status}`}
              group={g}
              status={status}
              initiallyOpen={openRule ? openRule === g.ruleCode : i === 0}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}
